/**
 * global-ypo-header.ts
 *
 * YPO Global Header — Web Component (Lit)
 *
 * Usage (any framework):
 *   <global-ypo-header
 *     user-id="12345"
 *     access-token="eyJ..."
 *     app-context="ypod"
 *     api-base-url="https://api.ypoconnect.org"
 *   ></global-ypo-header>
 *
 * Custom Events emitted (bubble + composed — cross Shadow DOM):
 *   'ypo-header:navigate'        → { href, label }
 *   'ypo-header:profile-loaded'  → { userId, profile }
 *   'ypo-header:auth-error'      → { status, message }
 *   'ypo-header:avatar-click'    → { dropdownOpen }
 *   'ypo-header:search'          → { key: 'search' }
 */

import {LitElement, html, unsafeCSS} from 'lit'
import {resolveConnectUrl, resolveM2mxUrl} from './utils'
import {isMobileAppBrowser, sendNativeLogout} from './mobile-bridge'
import {GLOBAL_HEADER_STYLES} from './global-header-styles'
import {customElement, property, state} from 'lit/decorators.js'
import {unsafeHTML} from 'lit/directives/unsafe-html.js'
import {
    CONNECT_BASE_URL,
    CONTEXT_TITLES,
    DEFAULT_API_BASE_URL,
    GLOBAL_HEADER_ITEMS,
    ICON_LINKS,
    M2MX_OVERVIEW_URL,
    M2MX_PROFILE_URL,
    USER_PROFILE_ATTRIBUTE,
    booleanConverter,
    countConverter,
    emit,
    gaDataLayerEvent,
    getAccountLinks,
    parseAppContextAttribute,
    parseUserProfileAttribute,
    serializeUserProfileAttribute,
    type AppContext,
    type GlobalHeaderAvatarClickEvent,
    type GlobalHeaderAuthErrorEvent,
    type GlobalHeaderNavigateEvent,
    type GlobalHeaderProfileLoadedEvent,
    type GlobalHeaderSearchEvent,
    type NavItem,
    type UserProfile,
} from './shared-config'

// Re-export for consumers that import countConverter from this module directly
export {countConverter} from './shared-config'

// Inline SVG assets at build time via Vite's ?raw import
import ypoLogoRaw from '../assets/YPO.svg?raw'
import ypoTriangleRaw from '../assets/YPO-triangle.svg?raw'
import searchRaw from '../assets/search.svg?raw'
import bellRaw from '../assets/Bell.svg?raw'
import messagesRaw from '../assets/Messages.svg?raw'
import settingsRaw from '../assets/settings.svg?raw'
import shieldRaw from '../assets/shield.svg?raw'
import userRaw from '../assets/user.svg?raw'
import logoutRaw from '../assets/logout.svg?raw'

// ─────────────────────────────────────────────
// The Web Component
// ─────────────────────────────────────────────

@customElement('global-ypo-header')
export class GlobalYpoHeader extends LitElement {

    // ── Public attributes (observed) ───────────────────────────

    static styles = unsafeCSS(GLOBAL_HEADER_STYLES)
    /** The authenticated user's YPO ID (ypoId from JWT). */
    @property({type: String, attribute: 'user-id'})
    userId = ''
    /** A valid bearer access token to call YPO APIs. */
    @property({type: String, attribute: 'access-token'})
    accessToken = ''
    /**
     * Optional full user profile override. When provided, the component skips the
     * member profile API call and renders from this object instead.
     * Accepts either a direct UserProfile assignment in JS or a JSON-stringified
     * UserProfile via the user-profile attribute.
     */
    @property({
        attribute: USER_PROFILE_ATTRIBUTE,
        converter: {
            fromAttribute: parseUserProfileAttribute,
            toAttribute: serializeUserProfileAttribute,
        },
    })
    userProfile: UserProfile | null = null
    /**
     * Identifies the hosting app AND drives the active tab highlight.
     * Accepts one or more comma-separated AppContext values, e.g. "connect" or "connect,resources".
     * Every context in the array is checked when computing active states for nav items and icon links.
     * Defaults to ['unknown'] (no tab selected).
     */
    @property({
        attribute: 'app-context',
        converter: {
            fromAttribute: parseAppContextAttribute,
            toAttribute: (val: AppContext[]): string => val.join(','),
        },
    })
    appContext: AppContext[] = ['unknown']
    /**
     * Base URL for the YPO member API.
     * Defaults to production — override for test/staging.
     */
    @property({type: String, attribute: 'api-base-url'})
    apiBaseUrl = DEFAULT_API_BASE_URL
    /**
     * When present, the header bar is hidden (display: none).
     */
    @property({type: Boolean, attribute: 'hidden-bar'})
    hiddenBar = false
    /**
     * When true, nav link clicks only emit 'ypo-header:navigate' — no actual
     * navigation occurs. The host app is responsible for routing.
     * When false (default), the component navigates to item.url in the same tab.
     */
    @property({type: Boolean, attribute: 'update-click-events-only'})
    updateClickEventsOnly = false
    /**
     * Show the Connect-app notification icons (bell + messages).
     * Default false — only enable on apps where these make sense (e.g. YPO Connect).
     */
    @property({type: Boolean, attribute: 'connect-icons'})
    connectIcons = false
    /**
     * Controls whether the scroll-triggered box-shadow is applied to the header.
     * Default true — set to false to permanently suppress the shadow (e.g. when the
     * host app renders the header inside an already-elevated surface).
     */
    @property({attribute: 'show-shadow', converter: booleanConverter, reflect: true})
    showShadow = true
    /**
     * Override base URL for all Connect-app links (those built from VITE_URL_CONNECT).
     * When set, any nav/icon/account link whose href starts with the compiled
     * VITE_URL_CONNECT value will have that prefix replaced with this value before
     * the 'ypo-header:navigate' event is fired and before window navigation occurs.
     * Useful when the host app runs Connect under a different hostname (e.g. local dev).
     */
    @property({type: String, attribute: 'connect-url'})
    connectUrl = ''

    // ── Internal reactive state ─────────────────────────────────
    /**
     * Unread notification count shown as a dot on the bell icon when value is more than 0.
     *
     * Vue 2 note: bind with :notifications="count" (setAttribute path).
     * The countConverter handles Vue 2's stringified edge cases ("undefined", "null", "NaN").
     */
    @property({attribute: 'notifications', converter: countConverter, reflect: true})
    notifications = 0
    /**
     * Unread message count shown as a badge on the messages icon when value is more than 0
     *
     * Vue 2 note: bind with :messages="count" (setAttribute path).
     * The countConverter handles Vue 2's stringified edge cases ("undefined", "null", "NaN").
     */
    @property({attribute: 'messages', converter: countConverter, reflect: true})
    messages = 0
    @state() private _userProfile: UserProfile | null = null
    @state() private _loading = false
    @state() private _hasAuthError = false
    @state() private _mobileOpen = false
    @state() private _abortController: AbortController | null = null
    @state() private _dropdownOpen = false
    @state() private _imgError = false

    // ── Lit lifecycle ───────────────────────────────────────────

    connectedCallback() {
        super.connectedCallback()
        document.addEventListener('click', this._handleOutsideClick)
        window.addEventListener('scroll', this._handleScroll, {passive: true})
        // Sync initial state (page may already be scrolled on mount)
        this.toggleAttribute('scrolled', window.scrollY > 10)
    }

    updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('userProfile')) {
            this._syncProvidedUserProfile()
            if (this.userProfile) return
        }

        const authChanged =
            changedProps.has('userId') || changedProps.has('accessToken') || changedProps.has('apiBaseUrl')

        if (authChanged && !this.userProfile && this.userId && this.accessToken) {
            this._imgError = false
            this._fetchUserProfile()
        }

        if (authChanged && !this.userProfile && (!this.userId || !this.accessToken)) {
            this._userProfile = null
            this._hasAuthError = false
            this._dropdownOpen = false
            this._imgError = false
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        this._abortController?.abort()
        document.removeEventListener('click', this._handleOutsideClick)
        window.removeEventListener('scroll', this._handleScroll)
    }

    // ── API ────────────────────────────────────────────────────

    render() {
        return html`
            <header class="gh-bar" role="banner">

                <!-- Desktop/Tablet: YPO Logo -->
                <a
                        class="gh-logo"
                        href=""
                        target="_self"
                        rel="noopener noreferrer"
                        aria-label="YPO Home"
                        @click="${(e: MouseEvent) => this._handleLinkClick({
                            text: 'YPO Home',
                            url: `${CONNECT_BASE_URL}/#/home`,
                            key: 'connect'
                        }, e)}"
                >
                    ${unsafeHTML(ypoLogoRaw)}
                </a>

                <!-- Desktop/Tablet: navigation — primary | divider | secondary -->
                <nav class="gh-nav" aria-label="YPO global navigation" @click="${() => this._dropdownOpen = false}">
                    <ul class="gh-nav-list" role="list">
                        ${GLOBAL_HEADER_ITEMS.primary.map(item => this._renderNavItem(item))}
                        <li class="gh-nav-divider" role="separator" aria-hidden="true"></li>
                        ${GLOBAL_HEADER_ITEMS.secondary.map(item => this._renderNavItem(item))}
                    </ul>
                </nav>

                <!-- Desktop/Tablet: right section — icon group + avatar -->
                <div class="gh-right" @click="${() => this._dropdownOpen = false}">

                    <!-- Icon group: bell, messages (conditional on connect-icons attr) -->
                    <div class="gh-icon-group" role="group" aria-label="Navigation icons">

                        <!-- Connect icons: divider + bell + messages (conditional on connect-icons attr) -->
                        ${this._renderConnectIcons()}

                    </div>

                    <!-- Avatar (initials → photo once profile loads) -->
                    ${this._renderAvatar()}

                </div>

                <!-- Mobile: left — YPO triangle + app context title -->
                <div class="gh-mobile-left">
                    <span class="gh-mobile-triangle" aria-hidden="true">
                        ${unsafeHTML(ypoTriangleRaw)}
                    </span>
                    <span class="gh-mobile-title">${CONTEXT_TITLES[this.appContext.find(c => c !== 'unknown') ?? 'unknown'] ?? 'YPO'}</span>
                </div>

                <!-- Mobile: right — search (conditional), member care, hamburger -->
                <div class="gh-mobile-right">

                    ${this._showSearchIcon() ? html`
                        <button
                                class="gh-mobile-icon-btn"
                                aria-label="Search"
                                @click="${this._handleSearchClick}"
                        >
                            ${unsafeHTML(searchRaw)}
                        </button>
                    ` : ''}

                    <button
                            class="gh-hamburger"
                            aria-label="${this._mobileOpen ? 'Close menu' : 'Open menu'}"
                            aria-expanded="${this._mobileOpen}"
                            aria-controls="gh-tray"
                            @click="${this._toggleMobile}"
                    >
                        <span class="gh-hamburger-line"></span>
                        <span class="gh-hamburger-line"></span>
                        <span class="gh-hamburger-line"></span>
                    </button>

                </div>

            </header>

            <!-- Mobile tray backdrop (click to close) -->
            <div
                    class="gh-tray-backdrop ${this._mobileOpen ? 'open' : ''}"
                    @click="${this._closeMobile}"
                    aria-hidden="true"
            ></div>

            <!-- Mobile slide-out tray -->
            ${this._renderMobileTray()}
        `
    }

    // ── Event handlers ─────────────────────────────────────────

    // ── Outside-click handler (bound so it can be removed) ──────
    private _handleOutsideClick = (e: MouseEvent) => {
        const path = e.composedPath()
        // Clicks on the profile info area (full name + recordTypeName) should never close the dropdown
        const profileEl = this.shadowRoot?.querySelector('.gh-dropdown-profile')
        if (profileEl && path.includes(profileEl)) {
            return
        }
        if (!path.includes(this)) {
            this._dropdownOpen = false
        }
    }

    // ── Scroll handler — toggles [scrolled] attribute on host ───
    private _handleScroll = () => {
        this.toggleAttribute('scrolled', window.scrollY > 10)
    }

    private async _fetchUserProfile() {
        this._abortController?.abort()
        this._abortController = new AbortController()

        this._loading = true
        this._hasAuthError = false

        try {
            const res = await fetch(
                `${this.apiBaseUrl}/profiles/api/persons/${this.userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    signal: this._abortController.signal,
                }
            )

            if (res.status === 401 || res.status === 403) {
                this._hasAuthError = true
                emit<GlobalHeaderAuthErrorEvent>(this, 'ypo-header:auth-error', {
                    status: res.status,
                    message: 'Access token is invalid or expired.',
                })
                return
            }

            if (!res.ok) {
                console.warn(`[global-ypo-header] Profile fetch returned ${res.status}`)
                return
            }

            const profile: UserProfile = await res.json()
            this._userProfile = profile

            emit<GlobalHeaderProfileLoadedEvent>(this, 'ypo-header:profile-loaded', {
                userId: this.userId,
                profile,
            })
        } catch (err) {
            if ((err as Error).name === 'AbortError') return
            console.warn('[global-ypo-header] Profile fetch failed:', err)
        } finally {
            this._loading = false
        }
    }

    private _syncProvidedUserProfile() {
        this._abortController?.abort()
        this._loading = false
        this._hasAuthError = false
        this._imgError = false

        if (!this.userProfile) {
            this._userProfile = null
            return
        }

        this._userProfile = this.userProfile

        emit<GlobalHeaderProfileLoadedEvent>(this, 'ypo-header:profile-loaded', {
            userId: this.userId || this.userProfile.id || '',
            profile: this.userProfile,
        })
    }

    private _handleLinkClick(item: NavItem, event: MouseEvent) {
        event.preventDefault()

        let effectiveUrl = item.url
        if (item.key === 'm2mx') {
            effectiveUrl = resolveM2mxUrl(
                this._userProfile?.recordTypeName,
                M2MX_PROFILE_URL,
                M2MX_OVERVIEW_URL,
            )
        }

        const resolvedUrl = this.connectUrl
            ? resolveConnectUrl(effectiveUrl, this.connectUrl, CONNECT_BASE_URL)
            : effectiveUrl

        const eventName = 'G_Nav_Link_Click';
        const eventValue = `${item.key} | page: ${window.location.href}`;

        gaDataLayerEvent(eventName, eventValue, { site_name: item.key })
        
        emit<GlobalHeaderNavigateEvent>(this, 'ypo-header:navigate', {
            href: resolvedUrl,
            key: item.key,
            label: item.text,
            context: item.context || null,
        })

        // Native shell branch: inside the mobile WebView, hand logout off to the
        // native host and stop. This is a terminal native handoff — not a URL the
        // host router can navigate — so it runs even in updateClickEventsOnly mode.
        // Falls through to the normal flow if no bridge is present. See mobile-bridge.ts.
        if (item.key === 'logout' && isMobileAppBrowser() && sendNativeLogout()) {
            return
        }

        if (!this.updateClickEventsOnly && resolvedUrl) {
            window.location.href = resolvedUrl
        }
    }

    private _handleSearchClick() {
        emit<GlobalHeaderSearchEvent>(this, 'ypo-header:search', {key: 'search'})
    }

    private _handleAvatarClick(e: MouseEvent) {
        e.stopPropagation()
        this._dropdownOpen = !this._dropdownOpen
        emit<GlobalHeaderAvatarClickEvent>(this, 'ypo-header:avatar-click', {
            dropdownOpen: this._dropdownOpen,
        })
    }

    // ── Rendering helpers ───────────────────────────────────────

    private _toggleMobile() {
        this._mobileOpen = !this._mobileOpen
    }

    private _closeMobile() {
        this._mobileOpen = false
    }

    /** Returns true when the given context is present in the appContext array. */
    private _hasContext(ctx: AppContext): boolean {
        return this.appContext.includes(ctx)
    }

    /** Returns true for the primary app contexts that should show the search icon on mobile. */
    private _showSearchIcon(): boolean {
        return this._hasContext('connect') || this._hasContext('learn') || this._hasContext('lead') || this._hasContext('global')
    }

    private _accountLinks() {
        return getAccountLinks(window.location.href, this.appContext, {
            myAccount: settingsRaw,
            myProfile: userRaw,
            m2mx: shieldRaw,
            logout: logoutRaw,
        })
    }

    private _renderAvatarDropdown() {
        if (!this._dropdownOpen || !this._userProfile) return html``

        const fullName = `${this._userProfile.preferredName || this._userProfile.firstName} ${this._userProfile.lastName}`.trim()
        const role = this._userProfile.recordTypeName

        const accountLinks = this._accountLinks()
        const accountItems = Object.values(accountLinks).filter(l => l.key !== 'logout')
        const logoutItem = accountLinks.logout

        return html`
            <div class="gh-avatar-dropdown" role="menu" aria-label="Account menu">

                <!-- Top: full name + recordTypeName -->
                <div class="gh-dropdown-group">
                    <div class="gh-dropdown-profile" @click="${(e: MouseEvent) => e.stopPropagation()}">
                        <span class="gh-dropdown-name">${fullName}</span>
                        ${role && role.length > 0 ? html`<span class="gh-dropdown-role">${role}</span>` : ''}
                    </div>
                </div>

                <div class="gh-dropdown-divider" role="separator" aria-hidden="true"></div>

                <!-- Account links: My account, My profile, M2Mx -->
                <div class="gh-dropdown-group">
                    ${accountItems.map(link => html`
                        <button
                                class="gh-dropdown-item"
                                role="menuitem"
                                @click="${(e: MouseEvent) => {
                                    this._handleLinkClick(link, e)
                                    this._dropdownOpen = false
                                }}"
                        >
                            <span class="gh-dropdown-icon" aria-hidden="true">${unsafeHTML(link.icon as string)}</span>
                            <span class="gh-dropdown-item-text">${link.text}</span>
                        </button>
                    `)}
                </div>

                <div class="gh-dropdown-divider" role="separator" aria-hidden="true"></div>

                <!-- Log out -->
                <div class="gh-dropdown-group">
                    <button
                            class="gh-dropdown-item"
                            role="menuitem"
                            @click="${(e: MouseEvent) => {
                                this._handleLinkClick(logoutItem, e)
                                this._dropdownOpen = false
                            }}"
                    >
                        <span class="gh-dropdown-icon" aria-hidden="true">${unsafeHTML(logoutItem.icon as string)}</span>
                        <span class="gh-dropdown-item-text">${logoutItem.text}</span>
                    </button>
                </div>

            </div>
        `
    }

    private _renderAvatar() {
        if (this._loading) {
            return html`<span class="gh-avatar-placeholder" aria-label="Loading profile">
        <span class="gh-spinner" aria-hidden="true"></span>
      </span>`
        }

        if (this._hasAuthError) {
            return html`<span class="gh-avatar-placeholder" title="Session expired"
                              aria-label="Session expired"></span>`
        }

        if (this._userProfile) {
            const initials =
                `${(this._userProfile.preferredName?.[0] || this._userProfile.firstName?.[0]) ?? ''}${this._userProfile.lastName?.[0] ?? ''}`.toUpperCase()
            const fullName = `${this._userProfile.preferredName || this._userProfile.firstName} ${this._userProfile.lastName}`.trim()

            const avatarEl = (this._userProfile.profileImageUrl && !this._imgError)
                ? html`<img
                            class="gh-avatar"
                            src="${this._userProfile.profileImageUrl}"
                            alt="${fullName}"
                            title="${fullName}"
                            @error="${() => {
                                this._imgError = true
                            }}"
                    />`
                : html`<span class="gh-avatar-initials" title="${fullName}" aria-label="${fullName}">
              ${initials}
            </span>`

            return html`
                <div class="gh-avatar-wrap">
                    <button
                            class="gh-avatar-btn"
                            aria-label="Open account menu"
                            aria-expanded="${this._dropdownOpen}"
                            aria-haspopup="true"
                            @click="${this._handleAvatarClick}"
                    >
                        ${avatarEl}
                    </button>
                    ${this._renderAvatarDropdown()}
                </div>
            `
        }

        // No profile yet — show empty avatar ring
        return html`<span class="gh-avatar-placeholder" aria-label="User profile"></span>`
    }

    // ── Styles ─────────────────────────────────────────────────

    private _renderConnectIcons() {
        if (!this.connectIcons) return html``

        return html`
            <!-- Divider between static icons and connect-specific icons -->
            <span class="gh-icon-divider" role="separator" aria-hidden="true"></span>

            <!-- Bell / Notifications -->
            <span class="gh-icon-wrap">
                <a
                        class="gh-icon-btn gh-badge-wrap ${this._hasContext('notifications') ? 'active' : ''}"
                        href="#notifications"
                        aria-label="Notifications"
                        aria-current="${this._hasContext('notifications') ? 'page' : 'false'}"
                        @click="${(e: MouseEvent) => this._handleLinkClick(ICON_LINKS.notifications, e)}"
                >
                    ${unsafeHTML(bellRaw)}
                    ${this.notifications > 0 ? html`<span class="gh-notif-dot" aria-hidden="true"></span>` : ''}
                </a>
                <span class="gh-icon-tooltip" aria-hidden="true">Notifications</span>
            </span>

            <!-- Messages -->
            <span class="gh-icon-wrap">
                <a
                        class="gh-icon-btn gh-badge-wrap ${this._hasContext('messages') ? 'active' : ''}"
                        href="#messages"
                        aria-label="Messages${this.messages > 0 ? `, ${this.messages} unread` : ''}"
                        aria-current="${this._hasContext('messages') ? 'page' : 'false'}"
                        @click="${(e: MouseEvent) => this._handleLinkClick(ICON_LINKS.messages, e)}"
                >
                    ${unsafeHTML(messagesRaw)}
                    ${this.messages > 0 ? html`<span class="gh-notif-dot" aria-hidden="true"></span>` : ''}
                </a>
                <span class="gh-icon-tooltip" aria-hidden="true">Messages</span>
            </span>
        `
    }

    // ── Render ─────────────────────────────────────────────────

    private _renderNavItem(item: NavItem, onClick?: () => void) {
        const active = item.context !== undefined && this._hasContext(item.context)
        return html`
            <li class="gh-nav-item">
                <a
                        class="gh-nav-link ${active ? 'active' : ''}"
                        href="${item.url}"
                        aria-current="${active ? 'page' : 'false'}"
                        @click="${(e: MouseEvent) => {
                            this._handleLinkClick(item, e)
                            onClick?.()
                        }}"
                >
          <span class="gh-nav-text-wrap">
            <span class="gh-nav-label">${item.text}</span>
          </span>
                    <span class="gh-nav-active-bar" aria-hidden="true"></span>
                </a>
            </li>
        `
    }

    /** Renders a single nav link row inside the mobile tray. */
    private _renderTrayNavItem(item: NavItem) {
        const active = item.context !== undefined && this._hasContext(item.context)
        return html`
            <li class="gh-tray-nav-item">
                <a
                        class="gh-tray-nav-link ${active ? 'active' : ''}"
                        href="${item.url}"
                        aria-current="${active ? 'page' : 'false'}"
                        @click="${(e: MouseEvent) => {
                            this._handleLinkClick(item, e)
                            this._closeMobile()
                        }}"
                >
                    ${item.text}
                </a>
            </li>
        `
    }

    /** Renders the avatar image or initials for use inside the tray profile section. */
    private _renderTrayAvatar() {
        if (!this._userProfile) return html``
        const initials = `${(this._userProfile.preferredName?.[0] || this._userProfile.firstName?.[0]) ?? ''}${this._userProfile.lastName?.[0] ?? ''}`.toUpperCase()
        const fullName = `${this._userProfile.preferredName || this._userProfile.firstName} ${this._userProfile.lastName}`.trim()
        if (this._userProfile.profileImageUrl && !this._imgError) {
            return html`<img class="gh-avatar" src="${this._userProfile.profileImageUrl}" alt="${fullName}" @error="${() => { this._imgError = true }}" />`
        }
        return html`<span class="gh-avatar-initials" title="${fullName}" aria-label="${fullName}">${initials}</span>`
    }

    /**
     * Renders the right-side slide-out tray triggered by the mobile hamburger.
     * Contains: header (triangle + title + close), optional profile, nav groups,
     * icon links, and account links.
     */
    private _renderMobileTray() {
        const accountLinks = this._accountLinks()
        const accountItems = Object.values(accountLinks).filter(l => l.key !== 'logout')
        const logoutItem = accountLinks.logout

        return html`
            <div
                    id="gh-tray"
                    class="gh-tray ${this._mobileOpen ? 'open' : ''}"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                    aria-hidden="${!this._mobileOpen}"
            >
                <!-- Tray header: triangle logo + context title + close button -->
                <div class="gh-tray-header">
                    <button
                            class="gh-tray-close"
                            aria-label="Close menu"
                            @click="${this._closeMobile}"
                    >
                        <svg viewBox="0 0 16 16" height="20" width="20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>

                <!-- All nav items: primary + secondary together, no divider between them -->
                <ul class="gh-tray-nav" role="list">
                    ${[...GLOBAL_HEADER_ITEMS.primary, ...GLOBAL_HEADER_ITEMS.secondary].map(item => this._renderTrayNavItem(item))}
                </ul>

                <!-- Spacer pushes icon links and profile toward the bottom -->
                <div class="gh-tray-spacer"></div>

                <!-- Icon links: conditional Notification/Messages -->
                <ul class="gh-tray-icon-links" role="list">
                    ${this.connectIcons ? html`
                        <li class="gh-tray-icon-link-item">
                            <a
                                    class="gh-tray-icon-link ${this._hasContext('notifications') ? 'active' : ''}"
                                    href="#notifications"
                                    aria-label="Notifications"
                                    aria-current="${this._hasContext('notifications') ? 'page' : 'false'}"
                                    @click="${(e: MouseEvent) => {
                                        this._handleLinkClick(ICON_LINKS.notifications, e)
                                        this._closeMobile()
                                    }}"
                            >
                                <span class="gh-tray-icon-link-icon" aria-hidden="true">${unsafeHTML(bellRaw)}</span>
                                <span class="gh-tray-icon-link-text">Notification</span>
                                ${this.notifications > 0 ? html`<span class="gh-tray-notif-dot" aria-hidden="true"></span>` : ''}
                            </a>
                        </li>
                        <li class="gh-tray-icon-link-item">
                            <a
                                    class="gh-tray-icon-link ${this._hasContext('messages') ? 'active' : ''}"
                                    href="#messages"
                                    aria-label="Messages${this.messages > 0 ? `, ${this.messages} unread` : ''}"
                                    aria-current="${this._hasContext('messages') ? 'page' : 'false'}"
                                    @click="${(e: MouseEvent) => {
                                        this._handleLinkClick(ICON_LINKS.messages, e)
                                        this._closeMobile()
                                    }}"
                            >
                                <span class="gh-tray-icon-link-icon" aria-hidden="true">${unsafeHTML(messagesRaw)}</span>
                                <span class="gh-tray-icon-link-text">Messages</span>
                                ${this.messages > 0 ? html`<span class="gh-tray-notif-dot" aria-hidden="true"></span>` : ''}
                            </a>
                        </li>
                    ` : ''}
                </ul>

                <div class="gh-tray-divider" role="separator" aria-hidden="true"></div>

                <!-- Profile + account links (only when profile has loaded) -->
                ${this._userProfile ? html`
                    <div class="gh-tray-profile">
                        ${this._renderTrayAvatar()}
                        <div class="gh-tray-profile-info">
                            <span class="gh-tray-profile-name">
                                ${`${this._userProfile.preferredName || this._userProfile.firstName} ${this._userProfile.lastName}`.trim()}
                            </span>
                            ${this._userProfile.recordTypeName ? html`
                                <span class="gh-tray-profile-role">${this._userProfile.recordTypeName}</span>
                            ` : ''}
                        </div>
                    </div>
                    <ul class="gh-tray-account" role="list">
                        ${accountItems.map(link => html`
                            <li class="gh-tray-account-item">
                                <button
                                        class="gh-tray-account-btn"
                                        @click="${(e: MouseEvent) => {
                                            this._handleLinkClick(link, e)
                                            this._closeMobile()
                                        }}"
                                >
                                    <span class="gh-tray-account-icon" aria-hidden="true">${unsafeHTML(link.icon as string)}</span>
                                    <span class="gh-tray-account-text">${link.text}</span>
                                </button>
                            </li>
                        `)}
                    </ul>
                    <div class="gh-tray-divider" role="separator" aria-hidden="true"></div>
                    <ul class="gh-tray-account" role="list">
                        <li class="gh-tray-account-item">
                            <button
                                    class="gh-tray-account-btn"
                                    @click="${(e: MouseEvent) => {
                                        this._handleLinkClick(logoutItem, e)
                                        this._closeMobile()
                                    }}"
                            >
                                <span class="gh-tray-account-icon" aria-hidden="true">${unsafeHTML(logoutItem.icon as string)}</span>
                                <span class="gh-tray-account-text">${logoutItem.text}</span>
                            </button>
                        </li>
                    </ul>
                ` : ''}

            </div>
        `
    }
}

// ── TypeScript declaration for JSX / IntrinsicElements ──────
// Lets React/TSX files use <global-ypo-header ... /> without type errors.

declare global {
    interface HTMLElementTagNameMap {
        'global-ypo-header': GlobalYpoHeader
    }
}
